import {defaultGraphConfig} from './interactive-graph-model';
import { checkpointAnchorId, checkpointEvidenceIds } from "./interactive-activity-checkpoints";
import { InteractiveActivityBlock as Block, type InteractiveCompetencyCheckpoint } from "./interactive-activity";
import { InteractiveActivityPackage as Package } from "./interactive-activity-package";
import { CatalogCompetency, initialCatalog } from "./catalog";

export const blockLabels: Record<Block["type"], string> = {
  section: "Partie",
  text: "Texte / consigne",
  image: "Image",
  document: "Document",
  video: "Vidéo",
  animation: "Animation",
  simulation: "Simulation",
  lab_instruction: "Consigne expérimentale",
  measurement_table: "Tableau de mesures",
  graph_task: "Graphique interactif",
  molecule: "Molécule · modèles et formules",
  question: "Question",
};
export const answerLabels = {
  mcq: "QCM",
  true_false: "Vrai / faux",
  numeric: "Nombre avec unité",
  short_text: "Réponse courte",
  open: "Réponse développée",
  formula: "Formule / calcul",
};
export const newId = (prefix: string) =>
  `${prefix}-${crypto.randomUUID().slice(0, 8)}`;
export function readStudioCatalog(): {
  catalog: CatalogCompetency[];
  notice: string;
} {
  try {
    const saved = localStorage.getItem("competenceo-catalog-v3");
    if (!saved)
      return {
        catalog: initialCatalog,
        notice: "Référentiel intégré CompetenceO",
      };
    const catalog: unknown = JSON.parse(saved);
    if (
      !Array.isArray(catalog) ||
      catalog.some(
        (c) =>
          !c ||
          typeof c.id !== "string" ||
          typeof c.label !== "string" ||
          typeof c.code !== "string" ||
          typeof c.schoolLevel !== "string",
      )
    )
      throw new Error();
    return {
      catalog,
      notice: "Référentiel professeur enregistré dans CompetenceO",
    };
  } catch {
    return {
      catalog: [],
      notice:
        "Référentiel local illisible : ouvrez les paramètres CompetenceO pour le restaurer. L’ajout de compétences est indisponible.",
    };
  }
}
export function newBlock(type: Block["type"]): Block {
  const id = newId(type);
  switch (type) {
    case "section":
      return { id, type, heading: "Nouvelle partie", description: "" };
    case "text":
      return {
        id,
        type,
        content: "Votre texte ou consigne…",
        variant: "paragraph",
      };
    case "lab_instruction":
      return {
        id,
        type,
        content: "Décrivez le protocole expérimental…",
        safety: [],
      };
    case "question":
      return {
        id,
        type,
        prompt: "Votre question…",
        answerType: "open",
        correctionMode: "teacher",
        competencyIds: [],
      };
    case "measurement_table":
      return {
        id,
        type,
        prompt: "Complétez le tableau de mesures.",
        columns: [
          { id: newId("col"), label: "Mesure", unit: "", input: "number" },
        ],
        minRows: 3,
        maxRows: 6,
        competencyIds: [],
        correctionMode: "teacher",
      };
    case "molecule":
      return {id,type,molecule:{moleculeId:"ethanol",view:"ball-stick"},interactive:true};
    case "graph_task":
      return {
        id,
        type,
        prompt: "Tracez et exploitez le graphique.",
        graphConfig: defaultGraphConfig(),
        competencyIds: [],
        correctionMode: "teacher",
      };
    case "animation":
    case "simulation":
      return {
        id,
        type,
        prompt: "Décrivez le phénomène à observer…",
        controls: [],
        expectedRelationships: [],
      };
    default:
      return { id, type, assetId: newId("asset"), caption: "" };
  }
}
export function reconcileCompetencies(pkg: Package) {
  const ids = new Set<string>();
  pkg.interactive.blocks.forEach((b) => {
    if ("competencyIds" in b) b.competencyIds?.forEach((id) => ids.add(id));
  });
  pkg.interactive.competencyRules.forEach((r) => ids.add(r.competencyId));
  pkg.interactive.competencyCheckpoints?.forEach((c) => ids.add(c.competencyId));
  pkg.activity.competencyIds = [...ids];
}
export function remapCompetenciesForLevel(pkg: Package, catalog: CatalogCompetency[], schoolLevel: string) {
  const mapId = (id: string) => {
    const current = catalog.find((item) => item.id === id);
    if (!current) return undefined;
    return catalog.find((item) => item.schoolLevel === schoolLevel && item.code === current.code)?.id;
  };
  const mapIds = (ids?: string[]) => [...new Set((ids ?? []).map(mapId).filter((id): id is string => !!id))];

  pkg.activity.schoolLevel = schoolLevel;
  pkg.activity.theme = "";
  pkg.activity.chapter = "";

  for (const block of pkg.interactive.blocks)
    if ("competencyIds" in block && block.competencyIds)
      block.competencyIds = mapIds(block.competencyIds);

  const mergedRules = new Map<string, typeof pkg.interactive.competencyRules[number]>();
  for (const rule of pkg.interactive.competencyRules) {
    const competencyId = mapId(rule.competencyId);
    if (!competencyId) continue;
    const previous = mergedRules.get(competencyId);
    mergedRules.set(competencyId, previous
      ? {...previous, blockIds:[...new Set([...previous.blockIds,...rule.blockIds])]}
      : {...rule, competencyId});
  }
  pkg.interactive.competencyRules = [...mergedRules.values()];

  pkg.interactive.competencyCheckpoints = (pkg.interactive.competencyCheckpoints ?? [])
    .flatMap((checkpoint) => {
      const competencyId = mapId(checkpoint.competencyId);
      return competencyId ? [{...checkpoint, competencyId}] : [];
    });

  reconcileCompetencies(pkg);
}

export function withCheckpoint(pkg:Package,checkpoint:InteractiveCompetencyCheckpoint):Package{
 const next=structuredClone(pkg),checkpoints=next.interactive.competencyCheckpoints??[];
 const index=checkpoints.findIndex(c=>c.id===checkpoint.id);
 if(index<0)checkpoints.push(structuredClone(checkpoint));else checkpoints[index]=structuredClone(checkpoint);
 next.interactive.competencyCheckpoints=checkpoints;
 reconcileCompetencies(next);
 return next;
}
export function removeBlock(pkg: Package, id: string) {
  if (pkg.interactive.blocks.some(b => b.type === "question" && b.helpSnapshotSourceBlockId === id))
    throw new Error("Cette proposition est liée à une aide. Retirez d’abord ce lien.");
  if (pkg.interactive.competencyCheckpoints?.some((c) => checkpointEvidenceIds(c).includes(id) || checkpointAnchorId(c) === id))
    throw new Error("Cette question est utilisée par un checkpoint. Retirez-la de ses preuves ou déplacez le checkpoint avant de la supprimer.");
  const removed = pkg.interactive.blocks.find((b) => b.id === id);
  pkg.interactive.blocks = pkg.interactive.blocks.filter((b) => b.id !== id);
  if (
    removed &&
    "assetId" in removed &&
    removed.assetId &&
    !pkg.interactive.blocks.some(
      (b) => "assetId" in b && b.assetId === removed.assetId,
    )
  ) {
    pkg.interactive.assets = pkg.interactive.assets.filter(
      (a) => a.id !== removed.assetId,
    );
  }
  pkg.interactive.competencyRules = pkg.interactive.competencyRules
    .map((r) => ({ ...r, blockIds: r.blockIds.filter((b) => b !== id) }))
    .filter((r) => r.blockIds.length);
  pkg.interactive.blocks.forEach((b) => {
    if (b.type === "graph_task" && b.sourceTableBlockId === id) {
      delete b.sourceTableBlockId;
      delete b.xColumnId;
      delete b.yColumnId;
    }
  });
  reconcileCompetencies(pkg);
}
export function removeBlockFromStudio(pkg: Package, id: string) {
  const removedIndex = pkg.interactive.blocks.findIndex((b) => b.id === id);
  if (removedIndex < 0) return;

  // Studio deletion is user-facing: detach secondary references instead of
  // silently refusing to delete the visible block.
  pkg.interactive.blocks.forEach((b) => {
    if (b.type === "question" && b.helpSnapshotSourceBlockId === id)
      delete b.helpSnapshotSourceBlockId;
  });

  pkg.interactive.competencyCheckpoints = (pkg.interactive.competencyCheckpoints ?? [])
    .flatMap((checkpoint) => {
      const next = structuredClone(checkpoint);
      const evidence = checkpointEvidenceIds(next);
      const remainingEvidence = evidence.filter((blockId) => blockId !== id);
      const referenced = remainingEvidence.length !== evidence.length;

      if (referenced) {
        if (next.evidenceBlockIds !== undefined)
          next.evidenceBlockIds = [...remainingEvidence];
        if (next.questionIds !== undefined)
          next.questionIds = [...remainingEvidence];

        // A checkpoint with no evidence left has no pedagogical meaning.
        if (!remainingEvidence.length) return [];
      }

      if (checkpointAnchorId(next) === id) {
        const replacement = remainingEvidence.at(-1);
        if (!replacement) return [];
        if (next.positionAfterBlockId !== undefined)
          next.positionAfterBlockId = replacement;
        if (next.positionAfterQuestionId !== undefined)
          next.positionAfterQuestionId = replacement;
      }

      return [next];
    });

  removeBlock(pkg, id);

  // Keep explicit flow positions coherent after the block list shrinks.
  for (const checkpoint of pkg.interactive.competencyCheckpoints ?? []) {
    const anchor = checkpointAnchorId(checkpoint);
    if (checkpoint.flowPosition === undefined) continue;
    if (anchor) {
      const index = pkg.interactive.blocks.findIndex((b) => b.id === anchor);
      checkpoint.flowPosition = Math.max(0, index + 1);
    } else if (checkpoint.flowPosition > removedIndex) {
      checkpoint.flowPosition -= 1;
    }
  }
  reconcileCompetencies(pkg);
}

export function duplicateBlock(pkg: Package, id: string): string {
  const index = pkg.interactive.blocks.findIndex((b) => b.id === id);
  const block = structuredClone(pkg.interactive.blocks[index]);
  block.id = newId(block.type);
  // A duplicated media block owns its resource so later edits are independent.
  if ("assetId" in block && block.assetId) {
    const asset = pkg.interactive.assets.find((a) => a.id === block.assetId);
    if (asset) {
      const copy = { ...asset, id: newId("asset") };
      pkg.interactive.assets.push(copy);
      block.assetId = copy.id;
    }
  }
  pkg.interactive.blocks.splice(index + 1, 0, block);
  pkg.interactive.competencyRules.forEach((r) => {
    if (r.blockIds.includes(id)) r.blockIds.push(block.id);
  });
  return block.id;
}
export function safeMediaUrl(url?: string) {
  if (!url) return undefined;
  if (
    /^https?:\/\//i.test(url) ||
    /^\/(?!\/)/.test(url) ||
    /^data:(image\/(png|jpeg|gif|webp|svg\+xml)|video\/(mp4|webm)|application\/pdf|text\/html);base64,/i.test(
      url,
    )
  )
    return url;
  return undefined;
}
